/*
  SYNOR — spending credit, as a code.

  WHY A CODE AND NOT A BALANCE.
  GoKwik's checkout takes the discount code CreditsYard hands it and applies it
  to the whole cart, whatever is in it. The collection limit set inside
  CreditsYard is read by Shopify's own checkout and by nothing else, so a
  shopper could pay for ₹49 testers with credit earned on testers — the money
  went round in a circle and the shop paid for it every time. GoKwik confirmed
  in writing that the limit has to be enforced "at code-generation time", which
  is exactly where the Apps Script already enforces it: creditOffer_ mints a
  Shopify discount code scoped to the bottles-only collection, and GoKwik
  honours that scope when the code is typed into its own coupon box.

  Attaching the code to the cart instead does not work and must not be tried
  again: GoKwik then shows "You saved ₹x" and still charges the full amount.
  Reproduced twice. The customer has to paste it, so this file makes pasting
  it as short a job as possible — one tap to get it, one tap to copy.

  THREE RULES IT KEEPS, because a code that does not work is worse than none.
    1. Nothing is shown unless the bag holds something the code can pay for.
       The eligible subtotal comes from /cart.js with the testers (3ml) left
       out, the same way the endpoint leaves them out.
    2. The figure on screen is the endpoint's own `amount`, never a guess made
       here — so what the screen promises and what checkout takes off are the
       same number.
    3. If anything fails — no customer, no network, nothing to spend it on — it
       says so in plain words instead of printing a code.

  The endpoint keeps the books itself: doPost sees the SYNORCREDIT code on the
  paid order and spendCredit_ takes that much out of CreditsYard. Nothing here
  has to remember anything for that to happen.

  It mounts itself rather than being placed by the sections it appears in. The
  bag and the credit page are both long, heavily settled files, and a snippet
  that reaches in through one selector each is a smaller thing to go wrong than
  an edit to either of them.
*/
(function () {
  var CFG = window.SYNOR_CC || {};
  var EP  = CFG.ep || '';
  if (!EP) return;

  var KEY  = 'synor_credit_code';   /* the code we last minted */
  var WKEY = 'synor_wallet';        /* sa-wallet.js's cached balance */
  var LIFE = 50 * 60 * 1000;        /* the endpoint's window is an hour; stop
                                       short of it so nobody is handed a code
                                       that dies while they are typing */
  var TESTERS = ['3ml', '3 ml'];

  var WORDS = {
    use:    'Use my credit',
    ready:  'Your credit code is ready',
    copy:   'Copy',
    copied: 'Copied',
    say:    'Paste it in the coupon box at checkout to take [amt] off.',
    small:  'Works on 15ml, 50ml and 100ml bottles. Not on ₹49 testers.',
    bag:    'Go to my bag →',
    need:   'Credit pays for bottles. Put a 15ml, 50ml or 100ml bottle in your bag and the code appears here.',
    shop:   'Browse bottles →',
    wait:   'One moment…',
    fail:   'Could not reach your credit just now. Tap to try again.',
    none:   'Nothing to use on this bag yet.'
  };
  var SHOP_URL = CFG.shop || '/collections/all';

  function money(n) {
    n = Math.round((Number(n) || 0) * 100) / 100;
    return '₹' + (n % 1 === 0 ? n : n.toFixed(2));
  }
  function esc(t) {
    return String(t == null ? '' : t)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function read(k) { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch (e) { return null; } }
  function write(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) {} }
  function drop(k) { try { localStorage.removeItem(k); } catch (e) {} }

  /* ── who is this ──
     Signed in by email and Shopify tells us. Signed in by phone, which is the
     usual way here, and KwikPass leaves the id in localStorage a moment later,
     so it is worth a few looks before giving up. */
  function whoAmI(done) {
    var seed = CFG.cid || '';
    if (seed) return done(String(seed));
    var v = '';
    try { v = localStorage.getItem('kp_customer_id') || ''; } catch (e) {}
    if (v) return done(v);
    var n = 0;
    var t = setInterval(function () {
      var x = '';
      try { x = localStorage.getItem('kp_customer_id') || ''; } catch (e) {}
      if (x) { clearInterval(t); done(x); }
      else if (++n >= 12) { clearInterval(t); done(''); }
    }, 400);
  }

  /* ── what in this bag can credit pay for ──
     Everything except the testers, and the exact variant ids of those lines.
     The ids are the whole point: a bottles-only COLLECTION cannot keep credit
     off a tester, because 3ml is a variant of the bottle's own product and the
     collection holds products, not variants. Proven on a six-tester bag —
     Shopify took the money off. A list of variant ids cannot be mistaken.
     /cart.js gives paise. */
  function bagValue(done) {
    fetch('/cart.js', { cache: 'no-store', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        var paise = 0;
        var vids = [];
        (c.items || []).forEach(function (it) {
          var v = String(it.variant_title || '').toLowerCase().trim();
          var tester = TESTERS.indexOf(v) > -1;
          if (tester) return;
          paise += Number(it.final_line_price || 0);
          if (it.variant_id && vids.indexOf(it.variant_id) < 0) vids.push(it.variant_id);
        });
        done(Math.round(paise) / 100, vids);
      })
      .catch(function () { done(0, []); });
  }

  /* ── how much is in the wallet ──
     sa-wallet.js keeps the last answer in localStorage, but it only runs on the
     credit page — so a shopper who walks straight from a product to the bag has
     nothing saved, and relying on that cache alone hid the button from exactly
     the people most likely to use it. The cache is still read first, because it
     paints at once; when it is missing or belongs to someone else, the endpoint
     is asked. */
  function balance(cid, done) {
    var w = read(WKEY);
    if (w && w.d && String(w.id) === String(cid) && Number(w.d.balance) > 0) {
      return done(Number(w.d.balance));
    }
    var cb = 'synorBag' + Date.now();
    var sc = document.createElement('script');
    var settled = false;
    function settle(d) {
      if (settled) return;
      settled = true;
      try { delete window[cb]; } catch (e) {}
      if (sc.parentNode) sc.parentNode.removeChild(sc);
      done(d && d.ok ? Number(d.balance) || 0 : 0);
    }
    window[cb] = settle;
    sc.onerror = function () { settle(null); };
    setTimeout(function () { settle(null); }, 15000);
    sc.src = EP + '?wallet=' + encodeURIComponent(cid) + '&cb=' + cb;
    sc.async = true;
    document.head.appendChild(sc);
  }

  /* ── ask for the code ──
     JSONP, because the Apps Script endpoint answers callbacks rather than
     cross-origin JSON, and because that is how the rest of this shop talks
     to it. */
  function mint(cid, rupees, vids, done) {
    var cb = 'synorCredit' + Date.now();
    var sc = document.createElement('script');
    var settled = false;
    function settle(d) {
      if (settled) return;
      settled = true;
      try { delete window[cb]; } catch (e) {}
      if (sc.parentNode) sc.parentNode.removeChild(sc);
      done(d);
    }
    window[cb] = settle;
    sc.onerror = function () { settle(null); };
    setTimeout(function () { settle(null); }, 20000);
    sc.src = EP + '?credit=' + encodeURIComponent(cid) +
             '&cart=' + encodeURIComponent(rupees) +
             '&vars=' + encodeURIComponent(vids.join(',')) + '&cb=' + cb;
    sc.async = true;
    document.head.appendChild(sc);
  }

  /* ── the look ──
     Injected once, so a page only has to render the snippet to get the whole
     thing — no second file to remember. */
  function styles() {
    if (document.getElementById('cc-css')) return;
    var st = document.createElement('style');
    st.id = 'cc-css';
    st.textContent =
      '.cc{margin:14px 0;font-family:inherit;color:#1A1712}' +
      '.cc[hidden]{display:none !important}' +
      '.cc-btn{display:block;width:100%;appearance:none;border:0;cursor:pointer;' +
        'border-radius:13px;padding:14px 16px;font:600 14px/1.2 inherit;letter-spacing:.02em;' +
        'background:#BE9A54;color:#FFFDF8;-webkit-tap-highlight-color:transparent}' +
      '.cc-btn:active{transform:scale(.995)}' +
      '.cc-btn[disabled]{opacity:.65;cursor:default}' +
      '.cc-hint{margin:9px 2px 0;font-size:11.5px;line-height:1.55;color:#8A7F6B}' +
      '.cc-card{border:1px solid #C8B98F;background:#FFFDF7;border-radius:14px;padding:15px}' +
      '.cc-ttl{font-size:9px;font-weight:700;letter-spacing:.18em;text-transform:uppercase;color:#6E6454}' +
      '.cc-row{margin-top:11px;display:flex;align-items:center;gap:9px;flex-wrap:wrap}' +
      '.cc-code{flex:1 1 150px;min-width:0;font:700 17px/1 ui-monospace,SFMono-Regular,Menlo,monospace;' +
        'letter-spacing:1.4px;background:#F4EEDE;border:1px dashed #B9A776;border-radius:9px;' +
        'padding:13px 10px;text-align:center;word-break:break-all}' +
      '.cc-copy{flex:0 0 auto;appearance:none;cursor:pointer;border:1px solid #1A1712;' +
        'background:#1A1712;color:#FFFDF8;border-radius:9px;padding:0 15px;height:44px;' +
        'font:600 13px/1 inherit;-webkit-tap-highlight-color:transparent}' +
      '.cc-copy.is-done{background:#0C6B52;border-color:#0C6B52}' +
      '.cc-say{margin:11px 0 0;font-size:12.5px;line-height:1.55;color:#4A4439}' +
      '.cc-small{margin:8px 0 0;font-size:11px;line-height:1.5;color:#8A7F6B}' +
      '.cc-link{display:inline-block;margin-top:12px;font-size:13px;font-weight:600;color:#8A6A33}' +
      '.cc-warn{color:#8C4A2F}' +
      '.cc-strip{display:flex;align-items:center;justify-content:space-between;gap:10px;' +
        'text-decoration:none;background:#FBF4E4;border:1px solid #E7D7B0;border-radius:11px;' +
        'padding:11px 13px;font:500 12.5px/1.35 inherit;color:#6B5126;' +
        '-webkit-tap-highlight-color:transparent}' +
      '.cc-strip b{font-weight:700;color:#8A6A33}' +
      '.cc-strip span{flex:none;font-size:15px;color:#A8873F}';
    document.head.appendChild(st);
  }

  /* ── one mount, one state machine ── */
  function run(box, where) {
    var cid = '';

    function paintCode(d) {
      box.innerHTML =
        '<div class="cc-card">' +
          '<div class="cc-ttl">' + esc(WORDS.ready) + '</div>' +
          '<div class="cc-row">' +
            '<span class="cc-code" data-cc-code>' + esc(d.code) + '</span>' +
            '<button type="button" class="cc-copy" data-cc-copy>' + esc(WORDS.copy) + '</button>' +
          '</div>' +
          '<p class="cc-say">' + WORDS.say.replace('[amt]', '<b>' + money(d.amount) + '</b>') + '</p>' +
          '<p class="cc-small">' + esc(WORDS.small) + '</p>' +
          (where === 'wallet' ? '<a class="cc-link" href="/cart">' + esc(WORDS.bag) + '</a>' : '') +
        '</div>';
    }
    function paintNeed() {
      box.innerHTML =
        '<div class="cc-card">' +
          '<p class="cc-say">' + esc(WORDS.need) + '</p>' +
          '<a class="cc-link" href="' + esc(SHOP_URL) + '">' + esc(WORDS.shop) + '</a>' +
        '</div>';
    }
    function paintButton(label, hint, warn) {
      box.innerHTML =
        '<button type="button" class="cc-btn" data-cc-go>' + esc(label) + '</button>' +
        (hint ? '<p class="cc-hint' + (warn ? ' cc-warn' : '') + '">' + esc(hint) + '</p>' : '');
    }

    /* copy, with the old selection trick behind the modern one: the clipboard
       API is refused inside some in-app browsers, and this shop lives in them */
    box.addEventListener('click', function (e) {
      var b = e.target && e.target.closest ? e.target.closest('[data-cc-copy]') : null;
      if (!b) return;
      e.preventDefault();
      var el = box.querySelector('[data-cc-code]');
      var txt = el ? el.textContent : '';
      function done() { b.textContent = WORDS.copied; b.classList.add('is-done'); }
      function legacy() {
        try {
          var ta = document.createElement('textarea');
          ta.value = txt;
          ta.setAttribute('readonly', '');
          ta.style.position = 'fixed';
          ta.style.top = '0';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          ta.setSelectionRange(0, txt.length);
          document.execCommand('copy');
          document.body.removeChild(ta);
          done();
        } catch (e2) {}
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(txt).then(done, legacy);
      } else { legacy(); }
    });

    box.addEventListener('click', function (e) {
      var b = e.target && e.target.closest ? e.target.closest('[data-cc-go]') : null;
      if (!b || b.disabled) return;
      e.preventDefault();
      b.disabled = true;
      b.textContent = WORDS.wait;
      bagValue(function (rupees, vids) {
        if (!(rupees > 0) || !vids.length) { paintNeed(); return; }
        mint(cid, rupees, vids, function (d) {
          if (!d || !d.ok || !d.code) { paintButton(WORDS.use, WORDS.fail, true); return; }
          if (!(Number(d.amount) > 0)) { paintButton(WORDS.use, WORDS.none, true); return; }
          write(KEY, { cid: cid, code: d.code, amount: d.amount, at: Date.now() });
          paintCode(d);
        });
      });
    });

    whoAmI(function (found) {
      cid = found;
      if (!cid) return;                     /* a stranger is shown nothing */

      var saved = read(KEY);
      var fresh = !!(saved && String(saved.cid) === String(cid) && saved.code &&
                     (Date.now() - Number(saved.at || 0)) < LIFE);
      if (saved && !fresh) drop(KEY);

      bagValue(function (rupees) {
        if (where === 'bag') {
          /* the bag only speaks when it has something to say: a bottle in it,
             and credit to spend on it */
          if (!(rupees > 0)) return;
          if (fresh) { box.hidden = false; paintCode(saved); return; }
          balance(cid, function (bal) {
            if (!(bal > 0)) return;
            box.hidden = false;
            paintButton(WORDS.use + ' · ' + money(bal), null);
          });
          return;
        }

        /* the credit page is the page about this, so it always speaks */
        box.hidden = false;
        if (fresh) { paintCode(saved); return; }
        if (!(rupees > 0)) { paintNeed(); return; }
        paintButton(WORDS.use, WORDS.small);
      });
    });
  }

  function box() {
    var d = document.createElement('div');
    d.className = 'cc';
    d.hidden = true;
    return d;
  }

  styles();

  /* the credit page — under the two tiles, above the order list */
  var inner = document.querySelector('[data-wallet] [data-cw-in]');
  var split = inner && inner.querySelector('.cw-split');
  if (split && split.parentNode) {
    var a = box();
    split.parentNode.insertBefore(a, split.nextSibling);
    run(a, 'wallet');
  }

  /* the product page — under the buy row.
     "Buy now" adds the bottle and opens GoKwik without ever passing through
     the bag, so a shopper who uses it never sees the code and later asks why
     their credit did nothing. Rather than touch that button — it works, and
     the funnel is not the place to experiment — the page simply says the money
     is there, and the tap goes to the bag, where the code is waiting.

     Shown only to someone who actually has credit; everyone else sees the
     product page exactly as before. */
  var row = document.querySelector('[data-buyrow]');
  if (row && row.parentNode) {
    var strip = box();
    row.parentNode.insertBefore(strip, row.nextSibling);
    /* The form is display:contents and its children are placed by CSS `order`,
       so being next in the DOM is not enough — without this the line floated
       to the very top of the column, above the product's own name. Copying the
       buy row's order puts it immediately after the button, where it belongs;
       ties fall back to document order, and we are the next node. */
    try {
      var ord = window.getComputedStyle(row).order;
      if (ord && ord !== '0') strip.style.order = ord;
    } catch (e) {}
    whoAmI(function (cid) {
      if (!cid) return;
      balance(cid, function (bal) {
        if (!(bal > 0)) return;
        strip.innerHTML =
          '<a class="cc-strip" href="/cart">' +
            '<em style="font-style:normal"><b>' + money(bal) + ' credit</b> — ' +
            'use it on any bottle at checkout</em>' +
            '<span aria-hidden="true">›</span>' +
          '</a>';
        strip.hidden = false;
      });
    });
  }

  /* the bag — in the money block, under "You pay" and above the payment
     choice. Lower than that and the phone pushes it past the small print,
     where nobody reads it; the fixed Checkout bar is no use as an anchor
     because it is not in the flow at all.

     The bag is assembled from several sections and has been rebuilt more than
     once, so the anchor is tried in order of preference rather than assumed:
     the bill's pay box, then its note, then the pill bar, then the older
     single-section cart. */
  if (location.pathname.indexOf('/cart') === 0) {
    var at = document.querySelector('.m4-paybox') ||
             document.querySelector('.m4-note') ||
             document.querySelector('form.m4-go') ||
             document.querySelector('.ct-note');
    if (at && at.parentNode) {
      var b2 = box();
      at.parentNode.insertBefore(b2, at);
      run(b2, 'bag');
    }
  }
})();
